import React, { useMemo, useState } from 'react'
import { useUnprocessedI9Forms } from 'queries/user/useUnprocessedI9Forms'
import { UnprocessedEVerifyI9Form } from 'api/everify/user_w2'
import { Flex, Box, Loading, Card, Text, Label, Select } from '@workwhile/ui'
import { DetailView } from './DetailView'
import { defs } from 'components/Shared/defs'
import { Link } from 'react-router-dom'
import { SearchFacet } from 'components/Shared/SearchFacet'

export default function UnprocessedI9FormsPage() {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [groupBy, setGroupBy] = useState<'state' | 'market' | 'date-asc'>(
    'date-asc'
  )
  const [selectedMarket, setSelectedMarket] = useState<string | null>(null)
  const [selectedState, setSelectedState] = useState<string | null>(null)

  const {
    data: {
      unprocessedEVerifyI9Forms: unprocessedForms = [],
      availableMarkets = [],
      availableMailingStates = [],
    } = {},
    isLoading: isLoadingUnprocessedEVerifyI9Forms,
  } = useUnprocessedI9Forms({
    market: selectedMarket || undefined,
    mailingState: selectedState || undefined,
  })

  // Compute options for filters using the available values from the API
  const marketOptions = useMemo(
    () =>
      availableMarkets.map((market) => ({
        label: market,
        value: market,
      })),
    [availableMarkets]
  )

  const stateOptions = useMemo(
    () =>
      availableMailingStates.map((state) => ({
        label: state,
        value: state,
      })),
    [availableMailingStates]
  )

  // Grouping logic
  const groupedForms = useMemo(() => {
    if (!unprocessedForms) return []

    if (groupBy === 'date-asc') {
      // Sort by submittedAt and group by date
      const sortedForms = [...unprocessedForms].sort((a, b) => {
        const dateA = a.submittedAt || ''
        const dateB = b.submittedAt || ''
        return dateA.localeCompare(dateB)
      })

      const grouped = sortedForms.reduce(
        (acc, form) => {
          let date = 'No Date'
          if (form.submittedAt) {
            const [year, month, day] = form.submittedAt
              .slice(0, 10)
              .split('-')
              .map(Number)
            date = new Date(year, month - 1, day).toLocaleDateString()
          }
          if (!acc[date]) acc[date] = []
          acc[date].push(form)
          return acc
        },
        {} as Record<string, UnprocessedEVerifyI9Form[]>
      )

      return Object.entries(grouped).sort(([a], [b]) => {
        if (a === 'No Date') return 1
        if (b === 'No Date') return -1
        return new Date(a).getTime() - new Date(b).getTime()
      })
    }

    // Existing grouping logic for state and market
    const grouped = unprocessedForms.reduce(
      (acc, form) => {
        let key: string
        if (groupBy === 'state') {
          key = form.mailingState || 'Unknown State'
        } else {
          key = form.market || 'Unknown Market'
        }
        if (!acc[key]) acc[key] = []
        acc[key].push(form)
        return acc
      },
      {} as Record<string, UnprocessedEVerifyI9Form[]>
    )

    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b))
  }, [unprocessedForms, groupBy])

  return (
    <Flex>
      {/* Sidebar */}
      <Flex
        flexDirection="column"
        style={{
          minWidth: '255px',
          borderRadius: defs.borderRadiusL,
        }}
        borderColor={defs.veryLightBlack}
        bg={defs.veryLightBlack}
        p={defs.paddingM}
        my={defs.paddingM}
        mr={defs.marginM}
      >
        {/* Filters */}
        <Flex
          flexDirection="column"
          style={{ rowGap: defs.marginM }}
          mb={defs.marginM}
        >
          <SearchFacet label="State">
            <Select
              options={stateOptions}
              placeholder="Select state"
              isClearable
              value={
                stateOptions.find((opt) => opt.value === selectedState) || null
              }
              onChange={(opt) => setSelectedState(opt ? opt.value : null)}
            />
          </SearchFacet>
          <SearchFacet label="Market">
            <Select
              options={marketOptions}
              placeholder="Select market"
              isClearable
              value={
                marketOptions.find((opt) => opt.value === selectedMarket) ||
                null
              }
              onChange={(opt) => setSelectedMarket(opt ? opt.value : null)}
            />
          </SearchFacet>
        </Flex>
      </Flex>
      {/* Main content area */}
      <Box width="100%" p={defs.paddingM}>
        {/* Group by controls */}
        <Box mb={defs.marginM}>
          <Text fontWeight="bold" mb={2}>
            Group by
          </Text>
          <Flex>
            <Label
              as="button"
              type="button"
              style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
              variant={groupBy === 'date-asc' ? 'primary' : 'none'}
              mr="2"
              onClick={() => setGroupBy('date-asc')}
            >
              Date
            </Label>
            <Label
              as="button"
              type="button"
              style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
              variant={groupBy === 'state' ? 'primary' : 'none'}
              mr="2"
              onClick={() => setGroupBy('state')}
            >
              State
            </Label>
            <Label
              as="button"
              type="button"
              style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
              variant={groupBy === 'market' ? 'primary' : 'none'}
              mr="2"
              onClick={() => setGroupBy('market')}
            >
              Market
            </Label>
          </Flex>
        </Box>
        <Flex>
          <Box pr={defs.marginM}>
            {isLoadingUnprocessedEVerifyI9Forms ? (
              <Loading />
            ) : (
              <Card>
                {groupedForms.map(([group, forms]) => (
                  <Box key={group} mb={3}>
                    <Text fontWeight="bold" mb={2}>
                      {group}
                    </Text>
                    {forms.map((form) => (
                      <Box
                        mb={2}
                        p={3}
                        backgroundColor="offWhite"
                        key={form.userId}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedUserId(form.userId)}
                        borderColor={
                          selectedUserId === form.userId
                            ? 'primary'
                            : 'transparent'
                        }
                        borderStyle="solid"
                        borderWidth={2}
                      >
                        <Flex flexDirection="column">
                          <Link to={`/users/${form.userId}`}>
                            {form.firstName} {form.lastName} ({form.userId})
                          </Link>
                          {form.requiresEVerifyNote ? (
                            <Label variant="error">Requires Note</Label>
                          ) : null}
                        </Flex>
                      </Box>
                    ))}
                  </Box>
                ))}
                {unprocessedForms?.length === 0 ? (
                  <Text>There are no pending forms to process.</Text>
                ) : null}
              </Card>
            )}
          </Box>
          <Box pl={defs.marginM}>
            {!selectedUserId ? (
              <Text>Please select a form to view details</Text>
            ) : (
              <DetailView userId={selectedUserId} />
            )}
          </Box>
        </Flex>
      </Box>
    </Flex>
  )
}
